import { scaleUtc } from 'd3';

import type { TimelineData, TimelineEntity, TimelineExitKind } from './types';

const normaliseDateString = (value: string): string => {
  if (/^\d{4}$/.test(value)) return `${value}-01-01`;
  if (/^\d{4}-\d{2}$/.test(value)) return `${value}-01`;
  return value;
};

/** `YYYY`, `YYYY-MM` and full ISO dates, read as UTC. */
const parseTimelineDate = (value?: string): number | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (trimmed === '') return null;
  const normalised = normaliseDateString(trimmed);
  const time = Date.parse(
    /^\d{4}-\d{2}-\d{2}$/.test(normalised)
      ? `${normalised}T00:00:00Z`
      : normalised
  );
  return Number.isNaN(time) ? null : time;
};

/** Horizontal run of the vertical edge a track starts or ends with. */
const EDGE = 1.5;
/** A track keeps at least this much straight run before its turn. */
const MIN_RUN = 20;
const MIN_TRACK = 1.5;
const ROW_GAP = 2;
const FAMILY_WIDTH = 6;
const DENSE_PITCH = 14;
const DENSE_FAMILY_WIDTH = 4;
const TRACK_WIDTH = 3;
const CORNER_RADIUS = 1.5;
const SHARP_CORNER = 0.25;
const MIN_PLOT_WIDTH = 120;
const MIN_PLOT_HEIGHT = 80;

export const TIMELINE_OTHER_ID = '__timeline_other_lane';

export type TimelineLayoutInput = {
  data: TimelineData | null;
  width: number;
  height: number;
  paddingLeft: number;
  paddingRight: number;
  paddingTop: number;
  paddingBottom: number;
  leadIn?: number;
  familyGapRows: number;
  familyPadRows: number;
  fitRows: { maxFamilies: number; pitch: number };
};

type TimelinePoint = { x: number; y0: number; y1: number };

export type TimelineFoldedCompany = {
  id: string;
  label: string;
  joinTime: number;
  leftTime: number | null;
  value: number;
  x: number;
  y: number;
  ownY: number;
  leftX: number | null;
  leftY: number | null;
};

export type TimelineTrack = {
  id: string;
  label: string;
  familyId: string;
  kind: 'family' | 'track' | 'other';
  /** Row centre. */
  y: number;
  startX: number;
  /** Where the straight run ends: the turn, the exit, or the right edge. */
  endX: number;
  /** Straight run as a filled outline. */
  path: string;
  points: TimelinePoint[];
  /** The drop into the parent's row; null for a family line or a departure. */
  elbowPath: string | null;
  elbowWidth: number;
  /** True when the track is cut by the left edge of the axis: it existed before it. */
  cropped: boolean;
  runWidth: number;
  /** Headcount today of everything on this track, tracks that joined it included. */
  value: number;
  ownValue: number;
  share: number;
  folded: TimelineFoldedCompany[];
  groupSpan: { x0: number; x1: number } | null;
  joinTime: number | null;
  targetLabel: string | null;
  elbowEnd: { x: number; y: number } | null;
  parentLabel: string | null;
  endTime: number;
  /**
   * A dated exit, drawn as a thin rise from the row that carried the company
   * at that date up to its own row.
   */
  exitLink: TimelinePoint | null;
  exitKind: TimelineExitKind | null;
  exitTo: string | null;
  /** True when it left because its parent did, not on its own. */
  leftWithParent: boolean;
};

export type TimelineFamily = {
  id: string;
  label: string;
  y0: number;
  y1: number;
  /** Row centre of the family's own line. */
  y: number;
  memberIds: string[];
};

export type TimelineTick = { x: number; label: string; major: boolean };

export type TimelineLayout = {
  isPlotSizeValid: boolean;
  families: TimelineFamily[];
  tracks: TimelineTrack[];
  ticks: TimelineTick[];
  plotLeft: number;
  plotRight: number;
  plotTop: number;
  plotBottom: number;
  domain: [number, number];
  /** Vertical distance between rows. */
  pitch: number;
};

const EMPTY_LAYOUT: TimelineLayout = {
  isPlotSizeValid: false,
  families: [],
  tracks: [],
  ticks: [],
  plotLeft: 0,
  plotRight: 0,
  plotTop: 0,
  plotBottom: 0,
  domain: [0, 0],
  pitch: 0,
};

type Rolled = { node: Node; entryTime: number; exitTime: number };

type Node = {
  entity: TimelineEntity;
  id: string;
  label: string;
  children: Node[];
  parent: Node | null;
  depth: number;
  join: number | null;
  started: number | null;
  ended: number | null;
  entry: number;
  exit: number;
  base: number;
  today: number;
  subtreeToday: number;
  kind: TimelineTrack['kind'];
  familyId: string;
  target: Node | null;
  mergeTime: number;
  startTime: number;
  rolled: Rolled[];
  above: Node[];
  below: Node[];
  row: number;
  y: number;
  /** The x at which the track turns; Infinity when it never does. */
  turnX: number;
  cornerT: number;
  points: TimelinePoint[];
};

const buildTree = (entities: TimelineEntity[]): Node[] => {
  const byId = new Map<string, TimelineEntity>();
  for (const entity of entities) {
    if (!byId.has(entity.id)) byId.set(entity.id, entity);
  }
  const parentOf = new Map<string, string | null>();
  for (const entity of byId.values()) {
    const parentId = entity.parentId;
    parentOf.set(
      entity.id,
      parentId !== undefined && parentId !== entity.id && byId.has(parentId)
        ? parentId
        : null
    );
  }
  for (const id of parentOf.keys()) {
    const seen = new Set<string>([id]);
    let cursor = parentOf.get(id) ?? null;
    while (cursor !== null) {
      if (seen.has(cursor)) {
        parentOf.set(id, null);
        break;
      }
      seen.add(cursor);
      cursor = parentOf.get(cursor) ?? null;
    }
  }
  const nodes = new Map<string, Node>();
  for (const entity of byId.values()) {
    nodes.set(entity.id, {
      entity,
      id: entity.id,
      label: entity.label ?? entity.id,
      children: [],
      parent: null,
      depth: 0,
      join: parseTimelineDate(entity.joinedAt),
      started: parseTimelineDate(entity.startedAt),
      ended: parseTimelineDate(entity.endedAt),
      entry: 0,
      exit: 0,
      base: Math.max(entity.value ?? 0, 0),
      today: 0,
      subtreeToday: 0,
      kind: 'track',
      familyId: '',
      target: null,
      mergeTime: Number.POSITIVE_INFINITY,
      startTime: 0,
      rolled: [],
      above: [],
      below: [],
      row: 0,
      y: 0,
      turnX: Number.POSITIVE_INFINITY,
      cornerT: 0,
      points: [],
    });
  }
  const roots: Node[] = [];
  for (const node of nodes.values()) {
    const parentId = parentOf.get(node.id) ?? null;
    const parent = parentId === null ? undefined : nodes.get(parentId);
    if (parent) {
      node.parent = parent;
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  }
  return roots;
};

const descendants = (node: Node): Node[] => [
  node,
  ...node.children.flatMap(descendants),
];

const majorTickEvery = (span: number): number => {
  if (span > 60) return 10;
  if (span > 25) return 5;
  if (span > 12) return 2;
  return 1;
};

const formatYear = (time: number): string =>
  String(new Date(time).getUTCFullYear());

const point = (px: number, py: number): string =>
  `${px.toFixed(2)},${py.toFixed(2)}`;

const roundedDrop = (
  atX: number,
  fromY: number,
  toY: number,
  t: number
): string => {
  const direction = Math.sign(toY - fromY);
  if (direction === 0) return `M${point(atX, fromY)}`;
  if (t < SHARP_CORNER) {
    return [
      `M${point(atX - 1, fromY)}`,
      `L${point(atX, fromY)}`,
      `L${point(atX, toY)}`,
      `L${point(atX + 1, toY)}`,
    ].join('');
  }
  return [
    `M${point(atX - t - 1, fromY)}`,
    `L${point(atX - t, fromY)}`,
    `Q${point(atX, fromY)} ${point(atX, fromY + direction * t)}`,
    `L${point(atX, toY - direction * t)}`,
    `Q${point(atX, toY)} ${point(atX + t, toY)}`,
    `L${point(atX + t + 1, toY)}`,
  ].join('');
};

const edgeProgress = (distance: number): number => {
  if (distance <= 0) return 0;
  if (distance >= EDGE) return 1;
  return distance / EDGE;
};

/**
 * Lays out a corporate group in swimlanes: every direct subsidiary is a lane,
 * every company a straight track on its own row, running from its founding
 * until the date it was acquired, where it drops into its parent's row.
 */
export const layoutTimeline = ({
  data,
  width,
  height,
  paddingLeft,
  paddingRight,
  paddingTop,
  paddingBottom,
  leadIn = 0,
  familyGapRows,
  familyPadRows,
  fitRows,
}: TimelineLayoutInput): TimelineLayout => {
  const plotRight = width - paddingRight;
  const plotTop = paddingTop;
  let plotBottom = height - paddingBottom;
  const plotHeight = plotBottom - plotTop;
  if (
    !data ||
    data.entities.length === 0 ||
    plotRight - paddingLeft < MIN_PLOT_WIDTH ||
    plotHeight < MIN_PLOT_HEIGHT
  ) {
    return EMPTY_LAYOUT;
  }

  const roots = buildTree(data.entities);
  const root = roots[0];
  if (!root) return EMPTY_LAYOUT;
  for (const extra of roots.slice(1)) {
    extra.parent = root;
    root.children.push(extra);
  }
  const all = descendants(root);

  const times: number[] = [];
  for (const node of all) {
    for (const value of [node.join, node.started, node.ended]) {
      if (value !== null) times.push(value);
    }
  }
  const dataStart =
    parseTimelineDate(data.startDate) ??
    (times.length > 0 ? Math.min(...times) : Date.UTC(2000, 0, 1));
  const explicitEnd = parseTimelineDate(data.endDate);
  const domainStart = dataStart;
  const cropping =
    times.some((time) => time < domainStart) &&
    plotRight - paddingLeft - leadIn >= MIN_PLOT_WIDTH;
  const plotLeft = paddingLeft + (cropping ? leadIn : 0);
  const plotWidth = plotRight - plotLeft;
  const domainEndRaw =
    explicitEnd ??
    (times.length > 0 ? Math.max(...times, Date.now()) : Date.now());
  const domainEnd = domainEndRaw > domainStart ? domainEndRaw : domainStart + 1;
  const clamp = (time: number): number =>
    Math.min(Math.max(time, domainStart), domainEnd);
  const xScale = scaleUtc()
    .domain([new Date(domainStart), new Date(domainEnd)])
    .range([plotLeft, plotRight]);
  const x = (time: number): number =>
    Math.min(Math.max(xScale(new Date(clamp(time))), plotLeft), plotRight);
  const timeAt = (atX: number): number =>
    domainStart + ((atX - plotLeft) / plotWidth) * (domainEnd - domainStart);

  const annotate = (node: Node, parent: Node | null, depth: number): void => {
    node.depth = depth;
    const floor = parent ? parent.entry : domainStart;
    node.entry = clamp(Math.max(floor, node.join ?? floor));
    node.exit = clamp(
      Math.min(parent ? parent.exit : domainEnd, node.ended ?? domainEnd)
    );
    node.today = node.exit >= domainEnd ? node.base : 0;
    for (const child of node.children) annotate(child, node, depth + 1);
    node.subtreeToday =
      node.today +
      node.children.reduce((sum, child) => sum + child.subtreeToday, 0);
  };
  annotate(root, null, 0);
  const groupTotal = root.subtreeToday;

  // ---- Which companies get a track; the rest are folded into an ancestor. ----
  const rollInto = (host: Node, child: Node): void => {
    const walk = (node: Node, entryFloor: number): void => {
      const entryTime = Math.max(entryFloor, node.join ?? entryFloor);
      host.rolled.push({
        node,
        entryTime: clamp(Math.max(entryTime, host.startTime)),
        exitTime: Math.min(node.exit, host.exit),
      });
      for (const grandchild of node.children) walk(grandchild, entryTime);
    };
    walk(child, host.startTime);
  };
  const rankChildren = (node: Node): Node[] =>
    [...node.children].sort((a, b) => b.subtreeToday - a.subtreeToday);
  const chosen = new Set<Node>();
  const rankedFamilies = rankChildren(root);
  const familyCap =
    rankedFamilies.length > fitRows.maxFamilies + 1
      ? fitRows.maxFamilies
      : rankedFamilies.length;
  const chosenFamilies = rankedFamilies.slice(0, familyCap);
  const lanes = familyCap + (rankedFamilies.length > familyCap ? 1 : 0);
  const capacity = Math.floor(
    plotHeight / fitRows.pitch -
      lanes * 2 * familyPadRows -
      Math.max(lanes - 1, 0) * familyGapRows
  );
  let budget = capacity - lanes;
  for (const family of chosenFamilies) chosen.add(family);
  const frontier = chosenFamilies.flatMap((family) => family.children);
  while (budget > 0 && frontier.length > 0) {
    let best = frontier[0] as Node;
    for (const candidate of frontier) {
      if (candidate.subtreeToday > best.subtreeToday) best = candidate;
    }
    frontier.splice(frontier.indexOf(best), 1);
    chosen.add(best);
    frontier.push(...best.children);
    budget -= 1;
  }
  const keepChild = (child: Node): boolean => chosen.has(child);

  const tracks: Node[] = [];
  const settle = (node: Node): void => {
    node.startTime = clamp(
      Math.min(node.started ?? node.join ?? domainStart, node.join ?? domainEnd)
    );
    if (node.depth === 1 && node.join === null) node.startTime = domainStart;
    tracks.push(node);
    for (const child of rankChildren(node)) {
      if (keepChild(child)) {
        child.familyId = node.familyId;
        settle(child);
      } else {
        rollInto(node, child);
      }
    }
  };

  const familyNodes: Node[] = [];
  const folded: Node[] = [];
  rankedFamilies.forEach((child) => {
    if (keepChild(child)) {
      child.kind = 'family';
      child.familyId = child.id;
      settle(child);
      familyNodes.push(child);
    } else {
      folded.push(child);
    }
  });
  if (folded.length > 0) {
    const bucket: Node = {
      ...root,
      entity: { id: TIMELINE_OTHER_ID },
      id: TIMELINE_OTHER_ID,
      label:
        folded.length === 1
          ? (folded[0]?.label ?? '')
          : `${folded.length} smaller subsidiaries`,
      children: [],
      parent: root,
      depth: 1,
      base: 0,
      today: 0,
      subtreeToday: folded.reduce((sum, node) => sum + node.subtreeToday, 0),
      kind: 'other',
      familyId: TIMELINE_OTHER_ID,
      rolled: [],
      above: [],
      below: [],
      points: [],
      startTime: domainStart,
      entry: domainStart,
      exit: domainEnd,
    };
    for (const node of folded) rollInto(bucket, node);
    bucket.startTime = Math.min(
      ...bucket.rolled.map((member) => member.entryTime)
    );
    tracks.push(bucket);
    familyNodes.push(bucket);
  }

  // ---- Where each track turns in, and into which row. ----
  for (const node of tracks) {
    if (node.kind !== 'track' || !node.parent) continue;
    const mergeTime = node.join ?? node.parent.startTime;
    let target = node.parent;
    while (
      target.kind === 'track' &&
      target.target !== null &&
      target.mergeTime <= mergeTime
    ) {
      target = target.target;
    }
    node.target = target;
    node.mergeTime =
      node.exit > mergeTime
        ? clamp(Math.max(mergeTime, target.startTime))
        : Number.POSITIVE_INFINITY;
    if (node.mergeTime !== Number.POSITIVE_INFINITY) {
      node.startTime = Math.min(node.startTime, node.mergeTime);
    }
  }
  for (const node of tracks) {
    if (node.kind !== 'track' || !node.target) continue;
    if (node.mergeTime === Number.POSITIVE_INFINITY) continue;
    const turnAt = x(node.mergeTime);
    if (turnAt - x(node.startTime) < MIN_RUN) {
      node.startTime = Math.min(
        node.startTime,
        timeAt(Math.max(turnAt - MIN_RUN, plotLeft))
      );
    }
  }
  const tributariesOf = new Map<Node, Node[]>();
  for (const node of tracks) {
    if (node.kind !== 'track' || !node.target) continue;
    const list = tributariesOf.get(node.target) ?? [];
    list.push(node);
    tributariesOf.set(node.target, list);
  }
  for (const list of tributariesOf.values()) {
    list.sort(
      (a, b) => a.mergeTime - b.mergeTime || b.subtreeToday - a.subtreeToday
    );
  }

  // ---- Rows: a track sits beside its parent, later joins further out. ----
  const rowsOf = (node: Node): Node[] => {
    const list = tributariesOf.get(node) ?? [];
    node.above = [];
    node.below = [];
    let aboveRows = 0;
    let belowRows = 0;
    for (const child of list) {
      const size = rowsOf(child).length;
      if (aboveRows <= belowRows) {
        node.above.push(child);
        aboveRows += size;
      } else {
        node.below.push(child);
        belowRows += size;
      }
    }
    return [
      ...[...node.above].reverse().flatMap(rowsOf),
      node,
      ...node.below.flatMap(rowsOf),
    ];
  };
  familyNodes.sort((a, b) => {
    if (a.kind === 'other') return 1;
    if (b.kind === 'other') return -1;
    return b.subtreeToday - a.subtreeToday;
  });
  const familyRows = familyNodes.map((family) => rowsOf(family));
  const rowCount = familyRows.reduce(
    (sum, rows) => sum + rows.length + 2 * familyPadRows,
    0
  );
  const gapRows = familyGapRows * Math.max(familyNodes.length - 1, 0);
  const pitch = fitRows.pitch;
  let used = pitch * (rowCount + gapRows);
  let familyPad = familyPadRows * pitch;
  if (used < plotHeight && familyNodes.length > 0) {
    familyPad += (plotHeight - used) / (2 * familyNodes.length);
    used = plotHeight;
  }
  plotBottom = Math.max(plotBottom, plotTop + used);
  let cursorY = plotTop + Math.max(plotHeight - used, 0) / 2;
  const families: TimelineFamily[] = [];
  familyRows.forEach((rows, index) => {
    const family = familyNodes[index];
    if (!family) return;
    const y0 = cursorY;
    cursorY += familyPad;
    rows.forEach((node, rowIndex) => {
      node.row = rowIndex;
      node.y = cursorY + (rowIndex + 0.5) * pitch;
    });
    cursorY += rows.length * pitch + familyPad;
    families.push({
      id: family.familyId,
      label: family.label,
      y0,
      y1: cursorY,
      y: family.y,
      memberIds: family.kind === 'other' ? folded.map((node) => node.id) : [],
    });
    cursorY += familyGapRows * pitch;
  });

  const dense = pitch < DENSE_PITCH;
  const familyWidth = dense
    ? DENSE_FAMILY_WIDTH
    : Math.min(FAMILY_WIDTH, Math.max(pitch - ROW_GAP, MIN_TRACK));
  const trackWidth = (node: Node): number =>
    node.kind === 'track' ? TRACK_WIDTH : familyWidth;
  const startXOf = (node: Node): number => x(node.startTime);

  // ---- Turns: straight down into the parent's row on the merge date. ----
  for (const node of tracks) {
    if (node.kind !== 'track' || !node.target) continue;
    if (node.mergeTime === Number.POSITIVE_INFINITY) continue;
    node.turnX = x(node.mergeTime);
    node.cornerT = Math.min(
      CORNER_RADIUS,
      Math.abs(node.target.y - node.y) / 2
    );
  }

  // ---- Headcount each track carries, today. ----
  const ramp = (atX: number, fromX: number, toX: number): number => {
    const rin = edgeProgress(atX - fromX);
    if (toX === Number.POSITIVE_INFINITY) return rin;
    return rin * edgeProgress(toX - atX);
  };
  const exitX = (time: number): number =>
    time >= domainEnd ? Number.POSITIVE_INFINITY : x(time);
  const ownAt = (node: Node, atX: number): number => {
    let total =
      node.kind === 'other'
        ? 0
        : node.base * ramp(atX, startXOf(node), exitX(node.exit));
    for (const member of node.rolled) {
      const r = ramp(atX, x(member.entryTime), exitX(member.exitTime));
      if (r > 0) total += member.node.base * r;
    }
    return total;
  };
  const deepFirst = [...tracks].sort((a, b) => b.depth - a.depth);
  const ownToday = new Map<Node, number>();
  const bandToday = new Map<Node, number>();
  for (const node of deepFirst) {
    const own = ownAt(node, plotRight);
    let band = own;
    for (const child of tributariesOf.get(node) ?? []) {
      if (plotRight >= child.turnX) band += bandToday.get(child) ?? 0;
    }
    ownToday.set(node, own);
    bandToday.set(node, band);
  }

  // ---- Every straight run as an outline, plus the turn into the parent. ----
  const xs = new Set<number>([plotLeft, plotRight]);
  const addEdge = (atX: number): void => {
    for (const sample of [atX, atX + EDGE]) {
      if (sample >= plotLeft && sample <= plotRight) xs.add(sample);
    }
  };
  for (const node of tracks) {
    addEdge(startXOf(node));
    if (node.exit < domainEnd) addEdge(x(node.exit) - EDGE);
    if (node.turnX !== Number.POSITIVE_INFINITY) {
      xs.add(node.turnX - node.cornerT);
      addEdge(node.turnX);
    }
    for (const member of node.rolled) {
      addEdge(x(member.entryTime));
      if (member.exitTime < domainEnd) addEdge(x(member.exitTime) - EDGE);
    }
  }
  const X = [...xs].sort((a, b) => a - b);
  const liveAt = (node: Node, atX: number): boolean =>
    (node.kind !== 'other' &&
      ramp(atX, startXOf(node), exitX(node.exit)) > 0) ||
    node.rolled.some(
      (member) => ramp(atX, x(member.entryTime), exitX(member.exitTime)) > 0
    );
  const straightEnd = (node: Node): number => {
    if (node.turnX !== Number.POSITIVE_INFINITY) {
      return node.turnX - node.cornerT;
    }
    if (node.exit < domainEnd) return x(node.exit);
    return plotRight;
  };
  for (const atX of X) {
    for (const node of tracks) {
      if (atX > straightEnd(node) + 0.001) continue;
      const half = liveAt(node, atX) ? trackWidth(node) / 2 : 0;
      node.points.push({ x: atX, y0: node.y - half, y1: node.y + half });
    }
  }

  const joinTimeOf = (node: Node): number | null => {
    if (node.kind === 'family') return node.join;
    if (node.kind === 'track' && node.mergeTime !== Number.POSITIVE_INFINITY) {
      return node.mergeTime;
    }
    return null;
  };
  const carrierAt = (host: Node, at: number): Node => {
    let carrier = host;
    while (
      carrier.target &&
      carrier.mergeTime !== Number.POSITIVE_INFINITY &&
      at >= carrier.mergeTime
    ) {
      carrier = carrier.target;
    }
    return carrier;
  };
  const foldedOf = (node: Node): TimelineFoldedCompany[] =>
    node.rolled.map((member) => {
      const carrier = carrierAt(node, member.entryTime);
      const left = member.exitTime < domainEnd;
      const leftCarrier = left ? carrierAt(node, member.exitTime) : null;
      return {
        id: member.node.id,
        label: member.node.label,
        joinTime: member.entryTime,
        leftTime: left ? member.exitTime : null,
        value: member.node.today,
        x: Math.max(x(member.entryTime), startXOf(carrier)),
        y: carrier.y,
        ownY: node.y,
        leftX: leftCarrier
          ? Math.max(x(member.exitTime), startXOf(leftCarrier))
          : null,
        leftY: leftCarrier ? leftCarrier.y : null,
      };
    });
  const groupSpanOf = (node: Node): TimelineTrack['groupSpan'] => {
    if (node.kind === 'other') return null;
    let from: number | null = null;
    if (node.kind === 'family') from = x(node.join ?? node.startTime);
    else if (node.mergeTime !== Number.POSITIVE_INFINITY) {
      from = x(node.mergeTime);
    }
    if (from === null) return null;
    const to = node.exit < domainEnd ? x(node.exit) : plotRight;
    return to - from > 0.5 ? { x0: from, x1: to } : null;
  };
  const withoutRepeats = (list: TimelinePoint[]): TimelinePoint[] =>
    list.filter((point, index) => {
      const before = list[index - 1];
      const after = list[index + 1];
      return !(
        before &&
        after &&
        before.y0 === point.y0 &&
        before.y1 === point.y1 &&
        after.y0 === point.y0 &&
        after.y1 === point.y1
      );
    });
  const trackResults: TimelineTrack[] = [];
  for (const node of tracks) {
    const alive = node.points.map((point) => point.y1 - point.y0 > 0.01);
    const first = alive.indexOf(true);
    if (first < 0) continue;
    const last = alive.lastIndexOf(true);
    const squared = (
      edge: TimelinePoint | undefined,
      like: TimelinePoint | undefined
    ): TimelinePoint[] =>
      edge && like ? [{ x: edge.x, y0: like.y0, y1: like.y1 }] : [];
    const points = withoutRepeats([
      ...squared(node.points[first - 1], node.points[first]),
      ...node.points.slice(first, last + 1),
      ...squared(node.points[last + 1], node.points[last]),
    ]);
    const runWidth = Math.max(
      ...points.map((sample) => sample.y1 - sample.y0),
      0
    );
    const top = points.map(
      (point) => `${point.x.toFixed(2)},${point.y0.toFixed(2)}`
    );
    const bottom = [...points]
      .reverse()
      .map((point) => `${point.x.toFixed(2)},${point.y1.toFixed(2)}`);
    const endSample = node.points[last];
    const endWidth = endSample ? endSample.y1 - endSample.y0 : 0;
    let elbowPath: string | null = null;
    let elbowEnd: TimelineTrack['elbowEnd'] = null;
    if (node.turnX !== Number.POSITIVE_INFINITY && node.target) {
      const targetY = node.target.y;
      elbowPath = roundedDrop(node.turnX, node.y, targetY, node.cornerT);
      elbowEnd = { x: node.turnX, y: targetY };
    }
    const exitCarrier =
      node.kind === 'track' && node.parent && node.exit < domainEnd
        ? carrierAt(node, node.exit)
        : node;
    const leftWithParent =
      node.parent !== null &&
      node.parent.exit < domainEnd &&
      node.parent.exit <= (node.ended ?? Number.POSITIVE_INFINITY);
    const today = node.exit >= domainEnd ? (bandToday.get(node) ?? 0) : 0;
    const own = node.exit >= domainEnd ? (ownToday.get(node) ?? 0) : 0;
    const startX = startXOf(node);
    trackResults.push({
      id: node.id,
      label: node.label,
      familyId: node.familyId,
      kind: node.kind,
      y: node.y,
      startX,
      endX: points[points.length - 1]?.x ?? startX,
      path: `M${[...top, ...bottom].join('L')}Z`,
      points,
      elbowPath,
      elbowWidth: elbowPath ? endWidth : 0,
      cropped: Math.abs(startX - plotLeft) <= 0.5,
      runWidth,
      value: today,
      ownValue: own,
      share: groupTotal > 0 ? today / groupTotal : 0,
      folded: foldedOf(node),
      groupSpan: groupSpanOf(node),
      joinTime: joinTimeOf(node),
      targetLabel: node.target?.label ?? null,
      elbowEnd,
      parentLabel: node.parent?.label ?? null,
      endTime: node.exit,
      exitLink:
        exitCarrier === node
          ? null
          : { x: x(node.exit), y0: node.y, y1: exitCarrier.y },
      exitKind: leftWithParent ? null : (node.entity.exitKind ?? null),
      exitTo: leftWithParent ? null : (node.entity.exitTo ?? null),
      leftWithParent,
    });
  }

  const startYear = new Date(domainStart).getUTCFullYear();
  const endYear = new Date(domainEnd).getUTCFullYear();
  const every = majorTickEvery(endYear - startYear);
  const ticks: TimelineTick[] = [];
  for (let year = startYear; year <= endYear; year += 1) {
    const atX = x(Date.UTC(year, 0, 1));
    if (atX <= plotLeft || atX >= plotRight) continue;
    ticks.push({
      x: atX,
      label: formatYear(Date.UTC(year, 0, 1)),
      major: year % every === 0,
    });
  }

  return {
    isPlotSizeValid: true,
    families,
    tracks: trackResults,
    ticks,
    plotLeft,
    plotRight,
    plotTop,
    plotBottom,
    domain: [domainStart, domainEnd],
    pitch,
  };
};
